// frontend/src/tour/steps.ts
// Schritt-Definitionen fuer die Tindra-gefuehrte In-App-Tour.
//
// Zwei Teile (werden unabhaengig gestartet):
//   Teil 1 = Begruessung + Navigation auf dem Landeplatz der TUER, danach die
//            tuerspezifische Strecke und zum Schluss die Dashboard-Strecke
//            (buildTourPart1(favoriteTab); Auto-Start beim ersten Login)
//   PART_2 = Erzaehler, Settings/Modelle, Profil, Welten
//            (Auto-Start ab message_count>=1, NACHDEM Part 1 erledigt ist)
//
// Jeder Schritt enthaelt NUR i18n-Keys + Ziel-Route + (optional) data-tour-anchor.
// Die Tour-Engine uebersetzt die Keys zur Laufzeit. data-tour-anchor-Werte
// muessen mit den Attributen in den jeweiligen Pages uebereinstimmen.

import type { FavoriteTab } from '../api/types';
import { favoriteTabPath } from '../auth/postAuthRedirect';
import type { TourStep } from './tourEngine';

// ─── Tour nach Tuer (W7-B) ───────────────────────────────────────────────────
// Die Reihenfolge von Teil 1 richtet sich nach dem Lieblings-Tab (E13): wer
// durch die Begleiter-Tuer kam, sieht ZUERST seine Gespraechsliste; wer durch
// die Abenteuer-Tuer kam, sieht das Dashboard wie bisher; wer auf „Entdecken"
// startet, sieht zuerst den Katalog. Danach folgt in ALLEN Faellen dieselbe
// Dashboard-Strecke, damit niemand den Einstieg ins erste Abenteuer verpasst.
//
// Die Zuordnung Tab -> Landeplatz liegt in auth/postAuthRedirect.ts; hier wird
// sie bewusst NICHT dupliziert, sondern importiert.

/**
 * Schritt 1 — Begruessung, zentriert (kein Anchor). Die Route wird erst beim
 * Bauen gesetzt, weil sie von der Tuer abhaengt.
 */
const INTRO_STEP: Omit<TourStep, 'route'> = {
  titleKey: 'tour.part1.intro.title',
  bodyKey: 'tour.part1.intro.body',
};

/**
 * Schritt 2 — Navigation erklaeren (Bereichswechsel).
 *
 * W7-B: Der frueher hier stehende `fallbackAnchor: 'header-nav'` ist ERSATZLOS
 * entfernt. Er stammte aus der Zeit vor E2/Q9, als es ab 600 px eine eigene
 * Desktop-Top-Nav im AppHeader gab; die ist weg (dort navigiert seither die
 * Rail), und `header-nav` existiert in KEINER Datei mehr — der Rueckfall war
 * also tot. Er wird auch nicht gebraucht: `data-tour-anchor="bottom-nav"` sitzt
 * in ALLEN drei Gestalten auf dem AppNav-Container (bar/rail/sidebar,
 * AppNav.tsx), ist also immer sichtbar da. Der Name bleibt historisch.
 */
const NAV_STEP: Omit<TourStep, 'route'> = {
  anchor: 'bottom-nav',
  titleKey: 'tour.part1.nav.title',
  bodyKey: 'tour.part1.nav.body',
};

/**
 * Die Dashboard-Strecke: „Neues Abenteuer" erklaeren und gemeinsam das erste
 * Abenteuer oeffnen. Der letzte Schritt ist `interactive: true` und adaptiv:
 * gibt es bereits eine Kampagne, wird die erste Karte hervorgehoben; sonst der
 * "Neues Abenteuer"-Button.
 */
const DASHBOARD_STEPS: TourStep[] = [
  {
    route: '/dashboard',
    anchor: 'dashboard-new-adventure',
    titleKey: 'tour.part1.new.title',
    bodyKey: 'tour.part1.new.body',
  },
  {
    route: '/dashboard',
    anchor: 'dashboard-first-campaign',
    fallbackAnchor: 'dashboard-new-adventure',
    interactive: true,
    titleKey: 'tour.part1.open.title',
    bodyKey: 'tour.part1.open.body',
  },
];

/**
 * Die Chats-Strecke (W7-B): Liste, „Neuer Begleiter", Profil. Die Anker sitzen
 * in ChatList.tsx (`chats-list` an der Liste, `chats-new-companion` seit R2-07
 * am Knopf im actions-Slot der Kopfzeile) und auf dem Menue-Schalter der
 * ERSTEN Zeile (`chats-profile`, ChatRow.menuTourAnchor). Fehlt die erste Zeile
 * (leere Liste), faellt Schritt 3 auf die Liste zurueck.
 */
export const CHATS_STEPS: TourStep[] = [
  {
    route: '/chats',
    anchor: 'chats-list',
    titleKey: 'tour.chats.list.title',
    bodyKey: 'tour.chats.list.body',
  },
  {
    route: '/chats',
    anchor: 'chats-new-companion',
    // R2-07 (D4): Der Knopf sitzt seit dem Kopfzeilen-Umbau im actions-Slot des
    // AppHeader statt im Listenkopf. Rendert die Shell die Kopfzeile in einem
    // Sonderfall nicht, faellt der Schritt auf die Liste zurueck statt still zu
    // zentrieren (Karte C Risiko 6).
    fallbackAnchor: 'chats-list',
    titleKey: 'tour.chats.new.title',
    bodyKey: 'tour.chats.new.body',
  },
  {
    route: '/chats',
    anchor: 'chats-profile',
    fallbackAnchor: 'chats-list',
    titleKey: 'tour.chats.profile.title',
    bodyKey: 'tour.chats.profile.body',
  },
];

/** Die Entdecken-Strecke (W7-B) — ein Schritt auf dem oeffentlichen Katalog. */
export const DISCOVER_STEPS: TourStep[] = [
  {
    route: '/discover',
    anchor: 'discover-list',
    titleKey: 'tour.discover.list.title',
    bodyKey: 'tour.discover.list.body',
  },
];

/**
 * Teil 1, gewichtet nach der Tuer (W7-B):
 *   Begruessung + Navigation auf dem Landeplatz der Tuer
 *   -> die tuerspezifische Strecke (chats/discover; bei 'adventures' keine)
 *   -> die gemeinsame Dashboard-Strecke.
 *
 * Fuer 'adventures' ergibt das exakt die vier Schritte von frueher — die
 * bestehende Strecke bleibt damit unveraendert.
 */
export function buildTourPart1(favoriteTab: FavoriteTab = 'adventures'): TourStep[] {
  // EINE Wahrheit fuer Tab -> Pfad (Karte C Risiko 5): favoriteTabPath.
  const route = favoriteTabPath({ favorite_tab: favoriteTab });
  const doorSteps = favoriteTab === 'chats'
    ? CHATS_STEPS
    : favoriteTab === 'discover'
      ? DISCOVER_STEPS
      : [];
  return [
    { ...INTRO_STEP, route },
    { ...NAV_STEP, route },
    ...doorSteps,
    ...DASHBOARD_STEPS,
  ];
}

/**
 * Die unveraenderte Abenteuer-Strecke — ausschliesslich fuer Tests und als
 * Rueckfall, wenn kein Lieblings-Tab bekannt ist.
 */
export const PART_1: TourStep[] = buildTourPart1('adventures');

/**
 * Part 2 — "der Rest" der App, nach der ersten Nachricht. Wird auto-gestartet
 * sobald Part 1 erledigt ist UND der User laut /api/onboarding/coin-status
 * mindestens MESSAGE_COUNT_THRESHOLD Nachrichten geschrieben hat. Wir
 * oeffnen KEIN bezahltes Chat, um keine Coins zu verbrauchen.
 */
export const PART_2: TourStep[] = [
  {
    // Erzaehler-Wizard hinter "Neues Abenteuer".
    route: '/dashboard',
    anchor: 'dashboard-new-adventure',
    titleKey: 'tour.part2.narrator.title',
    bodyKey: 'tour.part2.narrator.body',
  },
  {
    // KI-Modelle & Medien (Defaults fuer neue Abenteuer).
    // W7-B: Sektionsroute statt '/settings'. Der /settings-Index leitet ab
    // Container-Breite 720 auf die ERSTE Sektion um (SettingsIndexRedirect) —
    // dort gibt es die Medien-Karte nicht, der Anker waere unauffindbar und die
    // Bubble erschiene still zentriert. Auf dem Handy ist die Sektionsroute ein
    // regulaerer Direktaufruf mit Zurueck-Weg (SettingsSectionPane).
    route: '/settings/media',
    anchor: 'settings-models',
    titleKey: 'tour.part2.models.title',
    bodyKey: 'tour.part2.models.body',
  },
  {
    // Profil / Konto. Aus Part 1 herausgenommen, damit Part 1 sich auf den
    // eigentlichen Onboarding-Flow (Abenteuer starten) konzentriert.
    // W7-B: Der Anker `settings-profile` sitzt auf der Konto-Karte
    // (ProfileSections.tsx) und existiert damit — Karte C Risiko 6 ist
    // ueberholt. Nur die Route musste auf die Sektion gezogen werden (siehe
    // Schritt darueber).
    route: '/settings/account',
    anchor: 'settings-profile',
    titleKey: 'tour.part2.profile.title',
    bodyKey: 'tour.part2.profile.body',
  },
  {
    // Welten-Bereich (Kosmos, Wesen, Zeiten, Kulturen). Der Anker `world-header`
    // sitzt auf der PageTitle des Welt-Navigators (world-elements/index.tsx),
    // den /world als Listen-Pane rendert — er existiert also (Karte C Risiko 6
    // ist auch hier ueberholt).
    route: '/world',
    anchor: 'world-header',
    titleKey: 'tour.part2.world.title',
    bodyKey: 'tour.part2.world.body',
  },
];

/**
 * Live-Gespraech-Ministrecke (7.1.1) — EIN Schritt, der den neuen
 * Live-Talk-Button im ChatHeader erklaert (Anchor `chat-live-talk`,
 * gesetzt im ChatHeader). Die Chat-Route ist dynamisch (/chat/:id) und
 * `TourStep.route` ist Pflicht + statisch — deshalb liegt hier bewusst
 * ein Omit<TourStep,'route'>: der TourProvider fuellt `route` beim Start
 * mit dem AKTUELLEN Pfad, wodurch die Engine NICHT navigiert (startTour
 * rendert Schritt 1 direkt; Navigation passiert nur bei Route-Mismatch).
 * Nicht interactive: Abschluss ueber den normalen "Fertig"-Button.
 */
export const LIVE_TALK_STEP: Omit<TourStep, 'route'> = {
  anchor: 'chat-live-talk',
  titleKey: 'tour.live_talk_title',
  bodyKey: 'tour.live_talk_body',
};

/**
 * Wiedereinstiegs-Ministrecke (W3-A) — EIN Schritt auf der "Weiter"-Kachel
 * des Abenteuer-Tabs (Anchor `dashboard-continue`, gesetzt in
 * ContinueTile.tsx). Aufbau exakt wie LIVE_TALK_STEP: bewusst ein
 * Omit<TourStep,'route'>, der TourProvider fuellt `route` beim Start mit dem
 * AKTUELLEN Pfad — dadurch navigiert die Engine nicht und der Schritt
 * erscheint dort, wo der Nutzer ohnehin schon steht. Nicht interactive:
 * Abschluss ueber den normalen "Fertig"-Button.
 *
 * Reihenfolge: laeuft erst, wenn Part 1 UND Part 2 erledigt sind — die
 * bestehende Part-1/Part-2-Abfolge bleibt damit unberuehrt.
 */
export const CONTINUE_TILE_STEP: Omit<TourStep, 'route'> = {
  anchor: 'dashboard-continue',
  titleKey: 'tour.continue_title',
  bodyKey: 'tour.continue_body',
};

/**
 * Mindest-Anzahl an KI-Antworten, ab der Part 2 startet. Bewusst niedrig
 * (1) gesetzt: sobald der User seine erste Bot-Antwort erhalten hat, ist
 * der Core-Onboarding-Flow abgeschlossen und Part 2 darf erklaeren, was
 * es sonst noch gibt. Wert wird ebenfalls in /api/onboarding/coin-status
 * (buildCoinStatus) genutzt (Aktivitaets-Schwelle fuer die Coin-Onboarding-
 * Karte) — wir lesen ihn nur.
 */
export const MESSAGE_COUNT_THRESHOLD = 1;
